import { Button, Flex } from "antd";
import { UploadFilesRef } from "components/uploader/UploadFiles/";
import { AssetPurpose } from "pages/media/brandkit/components/BrandKitAssets/";
import {
  AssetCategory,
  AssetType,
  AssetUploader,
  MIME_TYPES,
} from "pages/media/brandkit/components/preview/AssetUploader";
import { BrandKitAsset } from "pages/media/types/";
import { StagedBrandKitAsset } from "types/";
import { useTranslate } from "#src/hooks";
import { useMemo, useState } from "react";
import { DragAndDropArea } from "./DragAndDropArea";

const MIME_TYPE_TO_ASSET: Record<
  keyof typeof MIME_TYPES,
  { assetType: AssetType; category: AssetCategory }
> = {
  image: { assetType: "Picture", category: "AssetImage" },
  video: { assetType: "Video", category: "Video" },
  music: { assetType: "Music", category: "Music" },
  font: { assetType: "Font", category: "Font" },
};

export type BrandkitAssetUploaderProps = {
  mimeType: keyof typeof MIME_TYPES;
  purpose: AssetPurpose;
  brandKitAssets?: BrandKitAsset[];
  stagedAssets?: StagedBrandKitAsset[];
  setStagedAssets?: React.Dispatch<React.SetStateAction<StagedBrandKitAsset[]>>;
  organizationId?: string;
  uploadRef?: React.RefObject<UploadFilesRef>;
  brandKitId?: string;
  onSave?: () => void;
  isSaving?: boolean;
  isDeleting?: boolean;
  isEditing?: boolean;
  onCancel?: () => void;
};

export const BrandkitAssetUploader = ({
  mimeType,
  purpose,
  brandKitAssets,
  stagedAssets,
  setStagedAssets,
  organizationId,
  uploadRef,
  brandKitId,
  onSave,
  isSaving,
  isDeleting,
  isEditing,
  onCancel,
}: BrandkitAssetUploaderProps) => {
  const t = useTranslate();
  const hasStagedAsset = useMemo(() => {
    return stagedAssets?.some((asset) => asset.purpose === purpose);
  }, [stagedAssets, purpose]);
  const [isUploading, setIsUploading] = useState(false);
  if (!brandKitId) return null;
  return (
    <Flex vertical gap={16}>
      <AssetUploader
        config={{
          assetType: MIME_TYPE_TO_ASSET[mimeType].assetType,
          category: MIME_TYPE_TO_ASSET[mimeType].category,
          acceptedMimeTypes: MIME_TYPES[mimeType],
          purposeValue: purpose,
        }}
        assets={brandKitAssets ?? []}
        stagedAssets={stagedAssets ?? []}
        setStagedAssets={setStagedAssets ?? (() => undefined)}
        organizationId={organizationId ?? ""}
        uploadRef={uploadRef}
        brandKitId={brandKitId}
        showPreviewOverride={false}
        hideUploadFeedback
        onUploadComplete={(files) => {
          if (!files[0]) return;
          setStagedAssets?.([{ uid: files[0].uid, purpose: purpose }]);
          onSave?.();
        }}
        onUploadingChange={(uploading) => {
          setIsUploading(uploading);
        }}
      >
        <DragAndDropArea inProgress={isSaving || isDeleting || isUploading} />
      </AssetUploader>
      {isEditing && !hasStagedAsset && (
        <Button onClick={onCancel} block>
          {t("buttons.cancel")}
        </Button>
      )}
    </Flex>
  );
};
